/**
 * MultiSelectSearchEditor
 * Configuration for multi-select-search control
 * Uses CONDITIONAL PATTERN: show filters conditionally
 */

import React, { useCallback } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';

interface MultiSelectSearchConfig {
  options?: Array<{ id: string; label: string }>;
  searchable?: boolean;
  showFilters?: boolean;
  filterFields?: string[];
  maxSelections?: number;
  allowCustom?: boolean;
  asyncSearch?: boolean;
  searchEndpoint?: string;
}

function renderMultiSelectSearchEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config || {}) as MultiSelectSearchConfig;

  const options = config.options || [];
  const searchable = config.searchable ?? true;
  const showFilters = config.showFilters ?? true;
  const filterFields = config.filterFields || [];
  const asyncSearch = config.asyncSearch ?? false;

  const handleAddOption = useCallback(() => {
    const newOptions = [...options, { id: `opt-${options.length + 1}`, label: `Option ${options.length + 1}` }];
    onConfigChange('options', newOptions);
  }, [options, onConfigChange]);

  const handleRemoveOption = useCallback(
    (index: number) => {
      onConfigChange('options', options.filter((_, i) => i !== index));
    },
    [options, onConfigChange],
  );

  const handleUpdateOption = useCallback(
    (index: number, field: 'id' | 'label', value: string) => {
      const updated = options.map((o, i) =>
        i === index ? { ...o, [field]: value } : o,
      );
      onConfigChange('options', updated);
    },
    [options, onConfigChange],
  );

  const handleFilterFieldsChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const fields = e.currentTarget.value
        .split('\n')
        .map((f) => f.trim())
        .filter((f) => f);
      onConfigChange('filterFields', fields);
    },
    [onConfigChange],
  );

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Multi-Select Search Configuration</h4>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={searchable}
            onChange={(e) => onConfigChange('searchable', e.target.checked)}
          />
          Enable Search
        </label>
      </div>

      {searchable && (
        <>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__checkbox-label">
              <input
                type="checkbox"
                checked={asyncSearch}
                onChange={(e) => onConfigChange('asyncSearch', e.target.checked)}
              />
              Async Search (from API)
            </label>
          </div>

          {asyncSearch && (
            <div className="af-properties-tab__group">
              <label htmlFor="search-endpoint-input" className="af-properties-tab__label">
                Search Endpoint
              </label>
              <input
                id="search-endpoint-input"
                type="text"
                value={config.searchEndpoint || ''}
                onChange={(e) => onConfigChange('searchEndpoint', e.target.value)}
                placeholder="https://api.example.com/search?q={query}"
                className="af-properties-tab__input"
                aria-label="Search endpoint"
              />
              <p className="af-properties-tab__info">Use {'{query}'} for search term</p>
            </div>
          )}
        </>
      )}

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={showFilters}
            onChange={(e) => onConfigChange('showFilters', e.target.checked)}
          />
          Show Filter Options
        </label>
      </div>

      {showFilters && (
        <div className="af-properties-tab__group">
          <label htmlFor="filter-fields-textarea" className="af-properties-tab__label">
            Filter Fields (one per line)
          </label>
          <textarea
            id="filter-fields-textarea"
            value={filterFields.join('\n')}
            onChange={handleFilterFieldsChange}
            placeholder="category&#10;status&#10;priority"
            rows={3}
            className="af-properties-tab__textarea"
            aria-label="Filter fields"
          />
        </div>
      )}

      <div className="af-properties-tab__group">
        <label htmlFor="max-selections-input" className="af-properties-tab__label">
          Maximum Selections (0 = unlimited)
        </label>
        <input
          id="max-selections-input"
          type="number"
          value={config.maxSelections || 0}
          onChange={(e) => onConfigChange('maxSelections', Number(e.target.value))}
          min="0"
          className="af-properties-tab__input"
          aria-label="Maximum selections"
        />
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={config.allowCustom ?? false}
            onChange={(e) => onConfigChange('allowCustom', e.target.checked)}
          />
          Allow Custom Values
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label" style={{ marginBottom: 6 }}>
          Options
        </label>
        {options.length === 0 && <p className="af-properties-tab__info">No options configured.</p>}
        {options.map((opt, i) => (
          <div key={i} className="af-properties-tab__option-row">
            <input
              type="text"
              value={opt.id}
              onChange={(e) => handleUpdateOption(i, 'id', e.target.value)}
              className="af-properties-tab__input af-properties-tab__option-id"
              placeholder="id"
              aria-label={`Option ${i + 1} ID`}
            />
            <input
              type="text"
              value={opt.label}
              onChange={(e) => handleUpdateOption(i, 'label', e.target.value)}
              className="af-properties-tab__input af-properties-tab__option-label"
              placeholder="label"
              aria-label={`Option ${i + 1} label`}
            />
            <button
              onClick={() => handleRemoveOption(i)}
              className="af-properties-tab__button af-properties-tab__button--danger"
              type="button"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          onClick={handleAddOption}
          className="af-properties-tab__button af-properties-tab__button--primary"
          type="button"
        >
          Add Option
        </button>
      </div>
    </div>
  );
}

export const MultiSelectSearchEditor: PropertyEditor = {
  type: 'multi-select-search',
  label: 'Multi-Select Search',
  description: 'Configure searchable multi-select with optional filters',
  render: renderMultiSelectSearchEditor,
};
